import * as Api from '@ViewModels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { CreateSkillAreaInput, SkillArea, UpdateSkillAreaInput } from '../../voice-agent/types/scenarios';
import { extractArrayFromResponse } from '../utils';

const SKILL_AREAS_QUERY_KEY = 'skillAreas';

/**
 * Fetch all skill areas
 */
export function useSkillAreas({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				SkillArea[] | { value?: SkillArea[]; values?: SkillArea[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/skillareas' }), 'GET');
			return extractArrayFromResponse<SkillArea>(response.value);
		},
		queryKey: [SKILL_AREAS_QUERY_KEY],
	});
}

/**
 * Fetch skill areas for the current user's role (determined server-side, defaults to SDR)
 * Use this for user-facing views like training dashboard and scenario selection.
 */
export function useMySkillAreas({ enabled = true }: { enabled?: boolean } = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				SkillArea[] | { value?: SkillArea[]; values?: SkillArea[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/skillareas/skillareas' }), 'GET');
			return extractArrayFromResponse<SkillArea>(response.value);
		},
		queryKey: [SKILL_AREAS_QUERY_KEY, 'skillareas'],
	});
}

/**
 * Fetch skill areas filtered by role
 * Use for admin use cases where you need data for a specific role regardless of current user.
 */
export function useSkillAreasByRole({ role, enabled = true }: { role: string; enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!role,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				SkillArea[] | { value?: SkillArea[]; values?: SkillArea[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/skillareas/role/${encodeURIComponent(role)}` }), 'GET');
			return extractArrayFromResponse<SkillArea>(response.value);
		},
		queryKey: [SKILL_AREAS_QUERY_KEY, 'role', role],
	});
}

/**
 * Fetch skill areas filtered by category ID
 */
export function useSkillAreasByCategory({ categoryId, enabled = true }: { categoryId: string; enabled?: boolean }) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!categoryId,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<
				SkillArea[] | { value?: SkillArea[]; values?: SkillArea[] }
			>(Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/skillareas/category/${categoryId}` }), 'GET');
			return extractArrayFromResponse<SkillArea>(response.value);
		},
		queryKey: [SKILL_AREAS_QUERY_KEY, 'category', categoryId],
	});
}

/**
 * Create a new skill area
 */
export function useCreateSkillAreaMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateSkillAreaInput): Promise<SkillArea> => {
			const response = await userSession.webServiceHelper.callWebServiceAsync<SkillArea>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'aida/skillareas' }),
				'POST',
				input
			);

			if (!response.value) {
				throw new Error('No data returned from create skill area API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to create skill area:', error);
		},
		onSuccess: () => {
			// Invalidate all skill area queries to refresh data
			queryClient.invalidateQueries({ queryKey: [SKILL_AREAS_QUERY_KEY] });
		},
	});
}

/**
 * Update an existing skill area
 */
export function useUpdateSkillAreaMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, input }: { id: string; input: UpdateSkillAreaInput }): Promise<SkillArea> => {
			if (!id) {
				throw new Error('Skill area ID is required');
			}

			const response = await userSession.webServiceHelper.callWebServiceAsync<SkillArea>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/skillareas/${id}` }),
				'PUT',
				input
			);

			if (!response.value) {
				throw new Error('No data returned from update skill area API');
			}

			return response.value;
		},
		onError: (error: Error) => {
			console.error('Failed to update skill area:', error);
		},
		onSuccess: () => {
			// Invalidate all skill area queries to refresh data
			queryClient.invalidateQueries({ queryKey: [SKILL_AREAS_QUERY_KEY] });
		},
	});
}

/**
 * Delete a skill area
 */
export function useDeleteSkillAreaMutation() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			if (!id) {
				throw new Error('Skill area ID is required');
			}

			await userSession.webServiceHelper.callWebServiceAsync(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `aida/skillareas/${id}` }),
				'DELETE'
			);
		},
		onError: (error: Error) => {
			console.error('Failed to delete skill area:', error);
		},
		onSuccess: () => {
			// Invalidate all skill area queries to refresh data
			queryClient.invalidateQueries({ queryKey: [SKILL_AREAS_QUERY_KEY] });
		},
	});
}
